import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Lightbulb, Lock, Send, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import TierBadge from "./TierBadge";
import { tierPrice, tierStyle } from "../lib/tiers";

const CATEGORIES = [
  { value: "portefeuilles", label: "Portefeuilles" },
  { value: "analyses", label: "Analyses" },
  { value: "cours", label: "Cours" },
  { value: "outils", label: "Outils" },
  { value: "site", label: "Site / compte" },
  { value: "autre", label: "Autre" },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

// Statut d'une idée : libellé + couleur
const STATUS = {
  new: { label: "Envoyée", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  review: { label: "À l'étude", cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  planned: { label: "Planifiée", cls: "bg-violet-500/15 text-violet-300 border-violet-500/40" },
  done: { label: "Réalisée", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  rejected: { label: "Non retenue", cls: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
};

const fmtDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return d;
  }
};

/**
 * Boîte à idées (page Mon compte). Réservée aux abonnés payants (essentiel+) et aux admins.
 * - locked : affiche la version verrouillée (floutée + invite à s'abonner), aucun appel API.
 * - isPreviewing / guardPreview : aperçu admin — l'envoi affiche un toast au lieu d'envoyer.
 */
/** Titre enregistré avec l'idée : le début du texte (le serveur en demande un, le formulaire n'en a plus). */
function titleFrom(text) {
  const first = text.trim().split("\n")[0].replace(/\s+/g, " ");
  if (first.length <= 60) return first;
  const cut = first.slice(0, 60);
  return `${cut.slice(0, cut.lastIndexOf(" ") > 30 ? cut.lastIndexOf(" ") : 60)}…`;
}

export default function IdeasBox({ locked = false, isPreviewing = false, guardPreview, cfg }) {
  const qc = useQueryClient();
  const [category, setCategory] = useState("portefeuilles");
  const [description, setDescription] = useState("");
  const [showErrors, setShowErrors] = useState(false);

  const { data: ideas = [] } = useQuery({
    queryKey: ["my-ideas"],
    queryFn: async () => (await api.get("/ideas/mine")).data,
    enabled: !locked,
  });

  const descErr = description.trim().length < 20 ? "Décrivez votre idée en au moins 20 caractères." : null;
  const invalid = !!descErr;

  const submitMut = useMutation({
    mutationFn: async () =>
      (await api.post("/ideas", { title: titleFrom(description), category, description: description.trim() })).data,
    onSuccess: () => {
      toast.success("Merci, votre idée a bien été envoyée");
      setCategory("portefeuilles");
      setDescription("");
      setShowErrors(false);
      qc.invalidateQueries({ queryKey: ["my-ideas"] });
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || "Impossible d'envoyer votre idée");
    },
  });

  const onSubmit = () => {
    if (guardPreview && guardPreview()) return;
    if (invalid) {
      setShowErrors(true);
      return;
    }
    submitMut.mutate();
  };

  return (
    <div className="relative rounded-xl border border-violet-500/25 bg-card p-6" data-testid="ideas-card" id="idees">
      {/* En-tête */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-lg bg-violet-500/15 border border-violet-500/30 text-violet-300 shrink-0">
            <Lightbulb className="w-5 h-5" />
          </span>
          <div>
            <h2 className="font-display font-bold text-lg">Une idée pour améliorer Side B ?</h2>
            <p className="mt-1 text-sm text-zinc-400">
              Une fonctionnalité, un sujet d'analyse, un outil… L'équipe lit tout et vous répond ici.
            </p>
          </div>
        </div>
        <TierBadge tier="essentiel" locked label="Dès Essentiel" />
      </div>

      {/* Contenu : flouté quand verrouillé */}
      <div className={locked ? "mt-6 pointer-events-none select-none blur-sm opacity-40" : "mt-6"} aria-hidden={locked}>
        {/* Formulaire */}
        {/* Pas de titre à écrire : la page ou le sujet concerné suffit, le titre est tiré du texte */}
        <div className="grid gap-3 sm:max-w-xs">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="rounded-md bg-white/5 border border-white/10 px-3 h-10 text-sm text-zinc-100 focus:outline-none focus:border-violet-500/50"
            data-testid="ideas-category"
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value} className="bg-[#0F0F10]">
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-3">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={1000}
            rows={4}
            placeholder="Votre idée"
            className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-zinc-100 resize-y focus:outline-none focus:border-violet-500/50"
            data-testid="ideas-description"
          />
          <div className="mt-1 text-right text-xs text-zinc-600">{description.length} / 1000</div>
        </div>

        {showErrors && descErr && (
          <div className="mt-1 space-y-0.5 text-xs text-rose-400">
            <div>{descErr}</div>
          </div>
        )}

        <div className="mt-3">
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitMut.isPending}
            className="inline-flex items-center gap-2 rounded-md text-sm font-medium h-10 px-4 shadow bg-emerald-500 text-black hover:bg-emerald-300 disabled:opacity-60"
            data-testid="ideas-submit"
          >
            {submitMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Envoyer l'idée
          </button>
        </div>

        {/* Liste des idées */}
        {ideas.length > 0 && (
          <div className="mt-7" data-testid="ideas-list">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-3">Vos idées</div>
            <div className="space-y-4">
              {ideas.map((idea) => {
                const st = STATUS[idea.status] || STATUS.new;
                return (
                  <div key={idea.id} className="rounded-lg border border-white/5 bg-white/[0.02] p-4" data-testid="ideas-item">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      {/* Titre affiché seulement s'il ne répète pas le début du texte (anciennes idées) */}
                      <div className="font-medium text-zinc-100">
                        {idea.description.startsWith(idea.title.replace(/…$/, "")) ? CAT_LABEL[idea.category] || idea.category : idea.title}
                      </div>
                      <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-[0.12em] ${st.cls}`}>
                        {st.label}
                      </span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-white/10 bg-white/[0.03] text-zinc-300">
                        {CAT_LABEL[idea.category] || idea.category}
                      </span>
                      <span>Envoyée le {fmtDate(idea.created_at)}</span>
                    </div>
                    <p className="mt-2 text-sm text-zinc-400 whitespace-pre-line">{idea.description}</p>
                    {idea.admin_reply && (
                      <blockquote className="mt-3 border-l-2 border-violet-500/50 pl-3 text-sm text-zinc-300">
                        <span className="text-violet-300 font-medium">{idea.replied_by || "Side B"}</span>
                        <span className="text-zinc-500"> · </span>
                        {idea.admin_reply}
                      </blockquote>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Surcouche verrouillée (compte gratuit) */}
      {locked && (
        <div className="absolute inset-0 grid place-items-center rounded-xl bg-background/40 backdrop-blur-[2px] p-6 text-center" data-testid="ideas-locked">
          <div className="max-w-sm">
            <span className="grid place-items-center w-11 h-11 mx-auto rounded-full bg-orange-500/15 border border-orange-500/40 text-orange-300">
              <Lock className="w-5 h-5" />
            </span>
            <h3 className="mt-4 font-display font-bold text-lg">Proposez vos idées à l'équipe</h3>
            <p className="mt-2 text-sm text-zinc-400">
              La boîte à idées est réservée aux abonnés. Dès l'offre Essentiel, suggérez des analyses, des outils ou des
              améliorations et suivez leur avancement.
            </p>
            <Link
              to="/upgrade"
              className={`mt-4 inline-flex items-center gap-2 rounded-md text-sm font-medium h-10 px-4 shadow ${tierStyle("essentiel").cta}`}
              data-testid="ideas-upgrade-btn"
            >
              Passer à Essentiel{tierPrice(cfg, "essentiel", " €/mois") ? ` · ${tierPrice(cfg, "essentiel", " €/mois")}` : ""}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
